import React from 'react';
import { DepthChartTab } from '../../data/sportRulesets';
import { TabNavigation } from './TabNavigation';
import { FormationSelector } from './FormationSelector';
import { DepthSelector } from './DepthSelector';
import { InningSelector } from './InningSelector';

interface DepthChartControlsProps {
  // Tab Navigation Props
  availableTabs: DepthChartTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  
  // Formation Selector Props
  sportRuleset: any;
  selectedFormation: string;
  availableFormations: any[];
  onFormationChange: (formationName: string) => void;
  onCreateFormation: () => void;
  onManageFormations: () => void;
  hasCustomFormations: boolean;
  isDefaultFormation: boolean;
  onSetAsDefault: (formationName: string, isDefault: boolean) => void;
  
  // Depth Selector Props
  depthStrings: { id: number; name: string; active: boolean }[];
  activeDepth: number;
  onDepthChange: (depthId: number) => void;
  onManageDepthStrings: () => void;
  
  // Inning Selector Props (Baseball/Softball)
  currentTeam: any;
  currentInning?: number;
  totalInnings?: number;
  onInningChange?: (inning: number) => void;
}

export const DepthChartControls: React.FC<DepthChartControlsProps> = ({
  // Tab Navigation
  availableTabs,
  activeTab,
  onTabChange,
  
  // Formation Selector
  sportRuleset,
  selectedFormation,
  availableFormations,
  onFormationChange,
  onCreateFormation,
  onManageFormations,
  hasCustomFormations,
  isDefaultFormation,
  onSetAsDefault,
  
  // Depth Selector
  depthStrings,
  activeDepth,
  onDepthChange,
  onManageDepthStrings,
  
  // Inning Selector
  currentTeam,
  currentInning,
  totalInnings,
  onInningChange
}) => {
  const shouldShowDepthSelector = activeTab !== 'Special Teams' && !(
    (currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && 
    (activeTab === 'Lineup' || activeTab === 'Fielding')
  );

  const shouldShowInningSelector = sportRuleset?.useInnings && activeTab === 'Fielding';

  return (
    <div className="flex flex-col space-y-2">
      {/* Tab Navigation */}
      <div>
        <TabNavigation
          availableTabs={availableTabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      </div>

      {/* Formation Controls Row */}
      <div className="flex flex-wrap gap-1 sm:gap-2 items-center">
        {/* Formation Dropdown */}
        {sportRuleset?.formations.enabled && (
          <FormationSelector
            selectedFormation={selectedFormation}
            availableFormations={availableFormations}
            onFormationChange={onFormationChange}
            onCreateFormation={onCreateFormation}
            onManageFormations={onManageFormations}
            showFormationManager={sportRuleset.formations.sportSpecificFormations || false}
            hasCustomFormations={hasCustomFormations}
            isDefaultFormation={isDefaultFormation}
            onSetAsDefault={onSetAsDefault}
          />
        )}
        
        {/* Inning selector for baseball/softball (Fielding tab) */}
        {shouldShowInningSelector && currentInning !== undefined && totalInnings !== undefined && onInningChange && (
          <InningSelector
            currentInning={currentInning}
            totalInnings={totalInnings}
            onInningChange={onInningChange}
          />
        )}
      </div>

      {/* Depth String Controls Row */}
      {shouldShowDepthSelector && (
        <div className="flex flex-wrap gap-1 sm:gap-2 items-center">
          <span className="text-sm text-gray-600">Depth Strings:</span>
          <DepthSelector
            depthStrings={depthStrings}
            activeDepth={activeDepth}
            onDepthChange={onDepthChange}
            onManageDepthStrings={onManageDepthStrings}
          />
        </div>
      )}
    </div>
  );
};